import type { ComponentType, SVGAttributes } from "react";

import {
  AirplaneIcon,
  AlertingIcon,
  AnalyticsIcon,
  AudienceIcon,
  BackpackIcon,
  BalloonIcon,
  BasketballIcon,
  BirthdayCakeIcon,
  BookIcon,
  BrainIcon,
  BubbleQuotesIcon,
  BulbSuggestionIcon,
  CameraIcon,
  CampaignsIcon,
  CandyIcon,
  CelebrateIcon,
  ChatIcon,
  CodeIcon,
  CoinsIcon,
  CompassIcon,
  CreditCardIcon,
  CrownIcon,
  CupIcon,
  DestinationIcon,
  DiamondIcon,
  DictationMicrophoneIcon,
  DollarIcon,
  DumbbellIcon,
  EditIcon,
  EmaileditorVideoIcon,
  ExperimentIcon,
  ExtensionsIcon,
  FavoriteIcon,
  FireIcon,
  FlagIcon,
  FolderIcon,
  GameControllerIcon,
  GhostIcon,
  GiftCardIcon,
  GiftIcon,
  GlobeIcon,
  GraduationCapIcon,
  HandWaveIcon,
  HeadphonesIcon,
  HeartBeatIcon,
  HeartDonationIcon,
  HourglassIcon,
  type IconAccessory,
  ImageIcon,
  KeyIcon,
  LightningBoltIcon,
  LockIcon,
  MagnetIcon,
  MailboxIcon,
  MailIcon,
  MedalIcon,
  MeetingIcon,
  MoneyBagIcon,
  MoneyHandIcon,
  MoonIcon,
  MountainBikeIcon,
  NumberSignIcon,
  PackageIcon,
  PaintBrushIcon,
  PercentIcon,
  PhoneIcon,
  PodcastIcon,
  ReceiptIcon,
  RocketIcon,
  ScissorsIcon,
  SendMessageLineIcon,
  ShieldIcon,
  ShoppingBagIcon,
  SmartwatchIcon,
  SnowflakeIcon,
  SparkleIcon,
  StageIcon,
  StarIcon,
  StopwatchIcon,
  StoreIcon,
  StylesIcon,
  SunIcon,
  TagIcon,
  TargetIcon,
  TicketIcon,
  TimeIcon,
  TreeIcon,
  TrendUpIcon,
  TrophyIcon,
  TruckIcon,
  UserHeartIcon,
  VideoClipIcon,
} from "@hightouch/htui";

export type ProjectGlyph = {
  /** The HTUI icon stem, which is what the stored string holds. */
  stem: string;
  /** What the glyph depicts, for labels and search. */
  label: string;
  Icon: ComponentType<SVGAttributes<SVGElement>>;
  /** Extra words the search matches, lower case. */
  keywords: string;
};

const glyph = (
  stem: string,
  label: string,
  Icon: ProjectGlyph["Icon"],
  keywords: string,
): ProjectGlyph => ({ stem, label, Icon, keywords });

/** The icons tab in grid order: what campaigns are about first, then the general set, then campaign moments. */
export const PROJECT_GLYPHS: readonly ProjectGlyph[] = [
  glyph("rocket", "Rocket", RocketIcon, "rocket launch startup"),
  glyph("target", "Target", TargetIcon, "target goal focus aim"),
  glyph(
    "campaigns",
    "Megaphone",
    CampaignsIcon,
    "megaphone promote announce campaign loud",
  ),
  glyph(
    "trend-up",
    "Trend line",
    TrendUpIcon,
    "chart growth statistics graph trend",
  ),
  glyph(
    "analytics",
    "Bar chart",
    AnalyticsIcon,
    "analytics analysis bars report",
  ),
  glyph("bulb-suggestion", "Light bulb", BulbSuggestionIcon, "idea light bulb"),
  glyph("sparkle", "Sparkle", SparkleIcon, "sparkle ai magic new"),
  glyph("star", "Star", StarIcon, "star favorite award"),
  glyph("trophy", "Trophy", TrophyIcon, "trophy win champion"),
  glyph("medal", "Medal", MedalIcon, "medal badge winner"),
  glyph("flag", "Flag", FlagIcon, "flag priority milestone"),
  glyph(
    "lightning-bolt",
    "Lightning bolt",
    LightningBoltIcon,
    "bolt lightning fast energy",
  ),
  glyph("fire", "Fire", FireIcon, "fire flame hot trending"),
  glyph("favorite", "Heart", FavoriteIcon, "heart love like loyalty"),
  glyph("gift", "Gift", GiftIcon, "gift present holiday"),
  glyph("shopping-bag", "Shopping bag", ShoppingBagIcon, "shopping bag retail"),
  glyph("tag", "Tag", TagIcon, "tag sale label price"),
  glyph("percent", "Percent", PercentIcon, "percent sale discount offer"),
  glyph("dollar", "Dollar", DollarIcon, "dollar money currency revenue"),
  glyph("money-bag", "Money bag", MoneyBagIcon, "money bag budget savings"),
  glyph("ticket", "Ticket", TicketIcon, "ticket event admit"),
  glyph("store", "Store", StoreIcon, "store shop"),
  glyph("package", "Package", PackageIcon, "package delivery product box"),
  glyph("truck", "Truck", TruckIcon, "truck delivery shipping"),
  glyph("mail", "Envelope", MailIcon, "email envelope mail newsletter"),
  glyph(
    "send-message-line",
    "Paper plane",
    SendMessageLineIcon,
    "paper plane send",
  ),
  glyph("phone", "Phone", PhoneIcon, "phone mobile sms"),
  glyph("alerting", "Bell", AlertingIcon, "bell notification push alert"),
  glyph("chat", "Chat bubble", ChatIcon, "bubble chat message"),
  glyph(
    "audience",
    "People",
    AudienceIcon,
    "people audience users customers team",
  ),
  glyph("camera", "Camera", CameraIcon, "camera photo shoot"),
  glyph("image", "Image", ImageIcon, "images photos pictures ai creative"),
  glyph("emaileditor-video", "Video", EmaileditorVideoIcon, "video film movie"),
  glyph("styles", "Palette", StylesIcon, "palette colors brand design"),
  glyph("edit", "Pencil", EditIcon, "pencil edit write copy"),
  glyph("Book", "Book", BookIcon, "books library"),
  glyph("folder", "Folder", FolderIcon, "folder files"),
  glyph("meeting", "Calendar", MeetingIcon, "calendar date schedule"),
  glyph("time", "Clock", TimeIcon, "clock time timer"),
  glyph("destination", "Map pin", DestinationIcon, "map pin location local"),
  glyph("globe", "Globe", GlobeIcon, "earth globe world international"),
  glyph("compass", "Compass", CompassIcon, "compass navigation explore"),
  glyph("airplane", "Airplane", AirplaneIcon, "airplane flight travel"),
  glyph("sun", "Sun", SunIcon, "sun summer day"),
  glyph("moon", "Moon", MoonIcon, "moon night"),
  glyph("snowflake", "Snowflake", SnowflakeIcon, "snow winter"),
  glyph("tree", "Tree", TreeIcon, "tree nature green"),
  glyph("cup", "Cup", CupIcon, "cup coffee tea"),
  glyph("balloon", "Balloon", BalloonIcon, "balloon party birthday"),
  glyph(
    "birthday-cake",
    "Birthday cake",
    BirthdayCakeIcon,
    "cake birthday anniversary",
  ),
  glyph("brain", "Brain", BrainIcon, "brain thinking research"),
  glyph("experiment", "Flask", ExperimentIcon, "lab experiment test science"),
  glyph("code", "Code", CodeIcon, "code developer"),
  glyph(
    "extensions",
    "Puzzle piece",
    ExtensionsIcon,
    "puzzle plugin integration",
  ),
  glyph("shield", "Shield", ShieldIcon, "shield security protection"),
  glyph("key", "Key", KeyIcon, "key password access"),
  glyph("lock", "Lock", LockIcon, "lock private"),
  glyph("diamond", "Diamond", DiamondIcon, "diamond premium pro"),
  glyph("crown", "Crown", CrownIcon, "crown vip"),
  glyph("dumbbell", "Dumbbell", DumbbellIcon, "dumbbell fitness training"),
  glyph("scissors", "Scissors", ScissorsIcon, "scissors cut"),
  glyph(
    "dictation-microphone",
    "Microphone",
    DictationMicrophoneIcon,
    "microphone podcast audio",
  ),
  glyph("number-sign", "Hashtag", NumberSignIcon, "hashtag social"),
  glyph("hand-wave", "Waving hand", HandWaveIcon, "welcome onboarding hello"),
  glyph(
    "hourglass",
    "Hourglass",
    HourglassIcon,
    "abandoned cart expiring urgency",
  ),
  glyph("magnet", "Magnet", MagnetIcon, "winback churn lapsed re-engage"),
  glyph("user-heart", "Person with heart", UserHeartIcon, "loyalty vip"),
  glyph("receipt", "Receipt", ReceiptIcon, "post-purchase order receipt"),
  glyph(
    "money-hand",
    "Hand with coins",
    MoneyHandIcon,
    "rewards cashback referral",
  ),
  glyph("gift-card", "Gift card", GiftCardIcon, "gift card voucher"),
  glyph("coins", "Coins", CoinsIcon, "rewards points"),
  glyph("stopwatch", "Stopwatch", StopwatchIcon, "flash sale urgency timer"),
  glyph("credit-card", "Credit card", CreditCardIcon, "billing payment card"),
  glyph("mailbox", "Mailbox", MailboxIcon, "direct mail postal"),
  glyph("podcast", "Podcast", PodcastIcon, "podcast audio"),
  glyph("paint-brush", "Paint brush", PaintBrushIcon, "brand design paint"),
  glyph(
    "bubble-quotes",
    "Quote bubble",
    BubbleQuotesIcon,
    "copy messaging quote",
  ),
  glyph("video-clip", "Film strip", VideoClipIcon, "video reel edit"),
  glyph("stage", "Stage", StageIcon, "event webinar stage"),
  glyph("celebrate", "Party popper", CelebrateIcon, "party launch anniversary"),
  glyph("ghost", "Ghost", GhostIcon, "halloween"),
  glyph("candy", "Candy", CandyIcon, "halloween treat"),
  glyph("backpack", "Backpack", BackpackIcon, "back to school"),
  glyph(
    "graduation-cap",
    "Graduation cap",
    GraduationCapIcon,
    "graduation school",
  ),
  glyph(
    "heart-donation",
    "Donation heart",
    HeartDonationIcon,
    "giving cause charity",
  ),
  glyph(
    "game-controller",
    "Game controller",
    GameControllerIcon,
    "gaming games",
  ),
  glyph("headphones", "Headphones", HeadphonesIcon, "music audio"),
  glyph("mountain-bike", "Bike", MountainBikeIcon, "outdoor bike"),
  glyph("smartwatch", "Smartwatch", SmartwatchIcon, "wearables tech watch"),
  glyph("heart-beat", "Heartbeat", HeartBeatIcon, "health fitness"),
  glyph("basketball", "Basketball", BasketballIcon, "sports"),
];

/** Ink first, then the board palette. Ink is stored as no colour at all. */
export const PROJECT_ICON_COLORS = [
  { label: "Ink", value: null },
  { label: "Red", value: "red.500" },
  { label: "Orange", value: "orange.500" },
  { label: "Yellow", value: "yellow.500" },
  { label: "Green", value: "green.500" },
  { label: "Blue", value: "blue.500" },
  { label: "Purple", value: "purple.500" },
  { label: "Pink", value: "pink.500" },
] as const;

export type ProjectIconColor = (typeof PROJECT_ICON_COLORS)[number]["value"];

/** The token a glyph renders in when its colour is ink. */
export const PROJECT_ICON_INK = "text.primary";

export type ProjectEmoji = { emoji: string; label: string; keywords: string };

const emoji = (value: string, keywords: string): ProjectEmoji => {
  const first = keywords.split(" ")[0] ?? value;
  return {
    emoji: value,
    label: first.charAt(0).toUpperCase() + first.slice(1),
    keywords,
  };
};

export const PROJECT_EMOJI: readonly ProjectEmoji[] = [
  emoji("🚀", "rocket launch"),
  emoji("🎯", "target goal"),
  emoji("📣", "megaphone announce"),
  emoji("📈", "chart growth"),
  emoji("💡", "idea bulb"),
  emoji("🛍️", "shopping bags"),
  emoji("🎁", "gift present"),
  emoji("🎄", "christmas tree holiday"),
  emoji("🎃", "pumpkin halloween"),
  emoji("💝", "heart gift valentine"),
  emoji("☀️", "sun summer"),
  emoji("🌸", "blossom spring flower"),
  emoji("🍂", "leaves autumn fall"),
  emoji("❄️", "snowflake winter"),
  emoji("🏷️", "tag sale label"),
  emoji("💰", "money bag budget"),
  emoji("📧", "email mail"),
  emoji("📱", "phone mobile"),
  emoji("🎨", "palette art brand"),
  emoji("🎬", "clapper film video"),
  emoji("📸", "camera photo"),
  emoji("✍️", "writing copy"),
  emoji("🧪", "test tube experiment"),
  emoji("🔍", "search research"),
  emoji("🤝", "handshake partner"),
  emoji("⭐", "star favorite"),
  emoji("🔥", "fire hot"),
  emoji("⚡", "bolt lightning"),
  emoji("🏆", "trophy win"),
  emoji("🌍", "globe world"),
  emoji("🧭", "compass explore"),
  emoji("🗓️", "calendar date"),
];

const GLYPH_PREFIX = "icon:";
const GLYPHS_BY_STEM = new Map(PROJECT_GLYPHS.map((g) => [g.stem, g]));
const COLORS_BY_VALUE = new Map<string, (typeof PROJECT_ICON_COLORS)[number]>(
  PROJECT_ICON_COLORS.flatMap((c) => (c.value ? [[c.value, c]] : [])),
);

export type ParsedProjectIcon =
  | { kind: "emoji"; emoji: string }
  | { kind: "glyph"; glyph: ProjectGlyph; color: ProjectIconColor };

/** Reads the stored string. An unknown stem or colour reads as no icon. */
export function parseProjectIcon(
  value: string | null | undefined,
): ParsedProjectIcon | null {
  if (!value) return null;
  if (!value.startsWith(GLYPH_PREFIX)) return { kind: "emoji", emoji: value };
  const [stem, color, ...rest] = value.slice(GLYPH_PREFIX.length).split(":");
  const found = stem ? GLYPHS_BY_STEM.get(stem) : undefined;
  if (!found || rest.length > 0) return null;
  if (color === undefined) return { kind: "glyph", glyph: found, color: null };
  const known = COLORS_BY_VALUE.get(color);
  return known ? { kind: "glyph", glyph: found, color: known.value } : null;
}

/** Writes a glyph choice as the stored string. */
export function glyphIcon(stem: string, color: ProjectIconColor): string {
  return color ? `${GLYPH_PREFIX}${stem}:${color}` : `${GLYPH_PREFIX}${stem}`;
}

/** A short name for a stored icon, for labels: "Rocket, orange", or the emoji itself. */
export function describeProjectIcon(
  value: string | null | undefined,
): string | null {
  const parsed = parseProjectIcon(value);
  if (!parsed) return null;
  if (parsed.kind === "emoji") return parsed.emoji;
  const color = parsed.color ? COLORS_BY_VALUE.get(parsed.color) : undefined;
  return color
    ? `${parsed.glyph.label}, ${color.label.toLowerCase()}`
    : parsed.glyph.label;
}

/** A stored glyph's component and colour, for HTUI icon slots. An emoji has none: projectLabel carries it as text. */
export function projectGlyphIcon(value: string | null | undefined) {
  const parsed = parseProjectIcon(value);
  return parsed?.kind === "glyph"
    ? { icon: parsed.glyph.Icon, color: parsed.color ?? PROJECT_ICON_INK }
    : null;
}

/** A stored icon as an option accessory for HTUI selects, so option labels stay plain text. */
export function projectIconAccessory(
  value: string | null | undefined,
): IconAccessory | undefined {
  const parsed = parseProjectIcon(value);
  if (!parsed) return undefined;
  if (parsed.kind === "emoji") return { type: "emoji", emoji: parsed.emoji };
  return {
    type: "icon",
    icon: parsed.glyph.Icon,
    color: parsed.color ?? PROJECT_ICON_INK,
  };
}

/** Whether a glyph or emoji matches the search text. An empty search matches everything. */
export function matchesProjectIconSearch(
  item: { label: string; keywords: string },
  query: string,
): boolean {
  const needle = query.trim().toLowerCase();
  if (!needle) return true;
  return (
    item.label.toLowerCase().includes(needle) || item.keywords.includes(needle)
  );
}
